import { useEffect, useRef, useCallback, useState } from 'react';

interface MobileBottomSheetProps {
  children: React.ReactNode;
  title?: string;
  onClose: () => void;
  snapPoints?: number[]; // percentages like [0.5, 0.75, 1]
  initialSnap?: number;
}

export function MobileBottomSheet({
  children,
  title,
  onClose,
  snapPoints = [0.5, 0.75, 1],
  initialSnap = 0,
}: MobileBottomSheetProps) {
  const sheetRef = useRef<HTMLDivElement>(null);
  const dragStartRef = useRef<{ y: number; height: number } | null>(null);
  const [currentSnap, setCurrentSnap] = useState(initialSnap);
  const currentHeight = snapPoints[currentSnap] ?? snapPoints[0];

  // Animate in on mount
  useEffect(() => {
    const sheet = sheetRef.current;
    if (!sheet) return;
    sheet.getBoundingClientRect();
    sheet.style.transform = 'translateY(0)';
  }, []);

  const handleClose = useCallback(() => {
    const sheet = sheetRef.current;
    if (!sheet) {
      onClose();
      return;
    }
    sheet.style.transform = 'translateY(100%)';
    setTimeout(onClose, 200);
  }, [onClose]);

  const handleDragStart = useCallback(
    (e: React.TouchEvent) => {
      const touch = e.touches[0];
      dragStartRef.current = {
        y: touch.clientY,
        height: currentHeight * window.innerHeight,
      };
    },
    [currentHeight],
  );

  const handleDragMove = useCallback((e: React.TouchEvent) => {
    const start = dragStartRef.current;
    if (!start) return;

    const touch = e.touches[0];
    const delta = touch.clientY - start.y;
    const sheet = sheetRef.current;
    if (!sheet) return;

    if (delta > 0) {
      // Dragging down — allow it
      sheet.style.transition = 'none';
      sheet.style.transform = `translateY(${delta}px)`;
    }
  }, []);

  const handleDragEnd = useCallback(
    (e: React.TouchEvent) => {
      const start = dragStartRef.current;
      if (!start) return;
      dragStartRef.current = null;

      const touch = e.changedTouches[0];
      const delta = touch.clientY - start.y;
      const sheet = sheetRef.current;
      if (!sheet) return;

      sheet.style.transition = 'transform 300ms cubic-bezier(0.22, 0.68, 0, 1)';

      if (delta > 100) {
        // Swiped down enough — dismiss or snap lower
        if (currentSnap === 0) {
          handleClose();
        } else {
          setCurrentSnap(currentSnap - 1);
          sheet.style.transform = 'translateY(0)';
        }
      } else if (delta < -80 && currentSnap < snapPoints.length - 1) {
        // Swiped up — snap higher
        setCurrentSnap(currentSnap + 1);
        sheet.style.transform = 'translateY(0)';
      } else {
        // Snap back
        sheet.style.transform = 'translateY(0)';
      }
    },
    [currentSnap, snapPoints.length, handleClose],
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-end"
      onClick={handleClose}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0"
        style={{
          backgroundColor: `rgba(0, 0, 0, ${0.3 + currentHeight * 0.2})`,
        }}
      />

      {/* Sheet */}
      <div
        ref={sheetRef}
        className="relative w-full rounded-t-xl flex flex-col"
        style={{
          backgroundColor: 'var(--dbgo-panel)',
          height: `${currentHeight * 100}vh`,
          transform: 'translateY(100%)',
          transition: 'transform 300ms cubic-bezier(0.22, 0.68, 0, 1)',
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag handle */}
        <div
          className="flex justify-center pt-3 pb-2 cursor-grab shrink-0"
          onTouchStart={handleDragStart}
          onTouchMove={handleDragMove}
          onTouchEnd={handleDragEnd}
        >
          <div
            className="rounded-full"
            style={{
              width: 40,
              height: 4,
              backgroundColor: 'var(--border-strong)',
            }}
          />
        </div>

        {/* Title */}
        {title && (
          <div className="px-4 pb-3 shrink-0">
            <div className="text-sm font-semibold text-text-primary">{title}</div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          {children}
        </div>
      </div>
    </div>
  );
}
